import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { Button } from 'primeng/button';
import { Popover } from 'primeng/popover';
import { LdEventStatus } from '@app/main/leak-detection/enums/ld-event-status.enum';
import { ldEventStatusLocalize } from '@app/main/leak-detection/localize/ld-event-status';
import { ldEventStatusIcon } from '@app/main/leak-detection/configs/ld-event-status.config';
import { getLdEventStatusBackground } from '@app/main/leak-detection/utils/ld-event-status-icon';
import { IconBadgeComponent } from '@app/shared/components/icon-badge/icon-badge.component';
import {
  DialogEventStatusAction,
  DialogEventStatusActionGroup,
} from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-status-action.enum';
import {
  dialogEventPipelineStages,
  dialogEventSeparatedActions,
  dialogEventStatusActionGroups,
  dialogEventStatusActions,
  dialogEventStatusTransitions,
  IDialogEventStatusTransition,
} from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-status-transitions.config';
import { DialogEventStatusMenuEntryKind } from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-status-menu-entry-kind.enum';
import { DialogEventState } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-state.service';
import { DialogEventTabsService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-tabs.service';

/** the gap the design leaves between the button and the menu it opens */
const DRAWER_GUTTER_PX = 8;

/** the stages that sit at the far end of the stepper, past what a narrow bar can show */
const SCROLL_TO_END_STAGES: ReadonlySet<LdEventStatus> = new Set([LdEventStatus.Investigation, LdEventStatus.Resolved]);

type StatusMenuAction = IDialogEventStatusTransition & { separatorBefore: boolean; background: string | undefined };

interface IStatusMenuEntryBase {
  separatorBefore: boolean;
}

interface IStatusMenuActionEntry extends IStatusMenuEntryBase {
  kind: DialogEventStatusMenuEntryKind.Action;
  action: StatusMenuAction;
}

interface IStatusMenuGroupEntry extends IStatusMenuEntryBase {
  kind: DialogEventStatusMenuEntryKind.Group;
  group: DialogEventStatusActionGroup;
  heading: { label: string; icon: string };
  background: string;
  actions: StatusMenuAction[];
}

type IStatusMenuEntry = IStatusMenuActionEntry | IStatusMenuGroupEntry;

@Component({
  selector: 'gc-dialog-event-status-bar',
  standalone: true,
  imports: [TranslateDirective, TranslatePipe, SvgIconComponent, Button, Popover, IconBadgeComponent],
  templateUrl: './dialog-event-status-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class DialogEventStatusBarComponent {
  protected readonly dialogState = inject(DialogEventState);
  private readonly tabs = inject(DialogEventTabsService);

  private readonly stepper = viewChild.required<ElementRef<HTMLElement>>('stepper');

  constructor() {
    effect(() => {
      const status = this.currentStatus();
      const strip = this.stepper().nativeElement;

      if (!SCROLL_TO_END_STAGES.has(status)) return;

      // one frame on, so the chips are laid out and `scrollWidth` is the real one
      requestAnimationFrame(() => {
        if (strip.scrollWidth > strip.clientWidth) strip.scrollLeft = strip.scrollWidth;
      });
    });
  }

  protected readonly isDrawerOpen = signal(false);

  protected readonly MenuEntryKind = DialogEventStatusMenuEntryKind;

  protected readonly stages = dialogEventPipelineStages;
  protected readonly stageLabels = ldEventStatusLocalize;
  protected readonly stageIcons = ldEventStatusIcon;

  protected readonly currentStatus = this.dialogState.status;

  private readonly actions = computed(() =>
    dialogEventStatusActions[this.currentStatus()].map(action => {
      const transition = dialogEventStatusTransitions[action];

      return {
        ...transition,
        separatorBefore: dialogEventSeparatedActions.has(action),
        background: transition.investigationResult === null ? getLdEventStatusBackground(transition.target) : undefined,
      };
    })
  );

  protected readonly entries = computed<IStatusMenuEntry[]>(() =>
    this.actions().reduce<IStatusMenuEntry[]>((entries, action) => {
      if (!action.group) {
        return [
          ...entries,
          { kind: DialogEventStatusMenuEntryKind.Action, separatorBefore: action.separatorBefore, action },
        ];
      }

      const last = entries.at(-1);

      if (last?.kind === DialogEventStatusMenuEntryKind.Group && last.group === action.group) {
        last.actions.push(action);

        return entries;
      }

      return [
        ...entries,
        {
          kind: DialogEventStatusMenuEntryKind.Group,
          group: action.group,
          separatorBefore: action.separatorBefore,
          heading: dialogEventStatusActionGroups[action.group],
          background: getLdEventStatusBackground(action.target),
          actions: [action],
        },
      ];
    }, [])
  );

  protected stageBackground(stage: LdEventStatus): string {
    return getLdEventStatusBackground(stage);
  }

  protected isActiveStage(stage: LdEventStatus): boolean {
    return stage === this.currentStatus();
  }

  protected selectAction(action: DialogEventStatusAction): void {
    const { instant, target } = dialogEventStatusTransitions[action];

    if (!instant) {
      this.tabs.openStatusChange(action);

      return;
    }

    this.dialogState.saveDraft({ ...this.dialogState.draft(), status: target }).subscribe();
  }

  protected alignAboveRight(popover: Popover): void {
    const container = popover.container;
    const target = popover.target as HTMLElement | undefined;

    if (!container || !target) return;

    const { top, right } = target.getBoundingClientRect();

    container.style.marginTop = '0';
    container.style.top = `${top + window.scrollY - container.offsetHeight - DRAWER_GUTTER_PX}px`;
    container.style.left = `${right + window.scrollX - container.offsetWidth}px`;
    container.style.transformOrigin = 'bottom right';
  }
}
